import CancelIcon from "@mui/icons-material/Cancel";
import CircleIcon from "@mui/icons-material/Circle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Dayjs } from "dayjs";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useContext, useState } from "react";

import ConfirmDialog from "../../../../components/dialogs/confirm";
import {
  MetaphoreCollectionSyncLog,
  MetaphoreCollectionSyncLogStatus,
} from "../../../../generated/gql/graphql";
import useScheduledSyncMutations from "../../../../hooks/mutation/sync/use-scheduled-sync-mutations";
import useCollectionSyncLogs from "../../../../hooks/query/sync/use-collection-sync-logs";
import useText from "../../../../hooks/use-text";
import { UserContext } from "../../../../providers/user-provider";
import { styles as filterStyles } from "../../../../sections/files/filters/metashot-type-filter";
import { ConfirmationModalType } from "../../../../types/confirm-modal";
import { LocalizedStrings, SupportedLanguages } from "../../../../utils/lang";

import { SyncLogDetailsModal, SyncLogThumbnails } from "./sync-log-assets";
import { formatSyncDate, getSyncStatusColor } from "./sync-log-utils";

const PAGE_SIZE = 10;

const ALL_STATUS = "All";

const DATE_FORMAT = "DD/MM/YYYY";

const dateFieldStyles = {
  fieldset: filterStyles.fieldset,
  ".MuiInputBase-input": filterStyles[".MuiSelect-select"],
  ".MuiInputLabel-root": filterStyles[".MuiInputLabel-root"],
};

type Props = {
  collectionId: string;
};

export default function SyncLogs(props: Props): ReactElement {
  const { collectionId } = props;

  const { strings } = useText(local);
  const { me } = useContext(UserContext);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUS);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [cancelModal, setCancelModal] = useState<ConfirmationModalType>();
  const [detailsLogId, setDetailsLogId] = useState<string>();

  const { data, loading, refetch } = useCollectionSyncLogs(collectionId, {
    filter: {
      ...(statusFilter !== ALL_STATUS
        ? { status: { eq: statusFilter as MetaphoreCollectionSyncLogStatus } }
        : {}),
      ...(fromDate || toDate
        ? {
            dateRange: {
              from: fromDate?.startOf("day").toISOString(),
              to: toDate?.endOf("day").toISOString(),
            },
          }
        : {}),
    },
    pagination: {
      page: page,
      limit: PAGE_SIZE,
    },
  });

  const {
    cancelLoading,
    runNowLoading,
    onCancelScheduledSync,
    onRunScheduledSyncNow,
  } = useScheduledSyncMutations();

  const result = data?.collectionSyncLogs;
  const logs = (result?.items ?? []) as MetaphoreCollectionSyncLog[];
  const totalCount = result?.totalCount ?? 0;
  const detailsLog = logs.find((v) => v.id === detailsLogId);

  async function handleCancel(id: string): Promise<void> {
    try {
      await onCancelScheduledSync(id);
      enqueueSnackbar(strings.cancelSuccess.label, { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.cancelError.label, { variant: "error" });
    }
    setCancelModal(undefined);
    refetch();
  }

  async function handleRunNow(id: string): Promise<void> {
    try {
      await onRunScheduledSyncNow(id);
      enqueueSnackbar(strings.runNowSuccess.label, { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.runNowError.label, { variant: "error" });
    }
    refetch();
  }

  function effectiveDate(log: MetaphoreCollectionSyncLog): string | undefined {
    return log.scheduledAt || log.dateCreated || undefined;
  }

  return (
    <div className="w-full">
      <div className="flex w-full mb-[25px] flex-wrap gap-[15px]">
        <Box sx={filterStyles}>
          <FormControl style={{ minWidth: 150, maxWidth: 200 }} size="small">
            <InputLabel className="text-[14px]">
              {strings.status.label}
            </InputLabel>
            <Select
              className="border-0 rounded-[15px] text-[14px] text-left"
              displayEmpty
              label={strings.status.label}
              value={statusFilter}
              onChange={(event) => {
                setPage(1);
                setStatusFilter(event.target.value);
              }}
              IconComponent={KeyboardArrowDownIcon}
            >
              <MenuItem value={ALL_STATUS}>{strings.allStatus.label}</MenuItem>
              {Object.values(MetaphoreCollectionSyncLogStatus).map((status) => (
                <MenuItem className="capitalize" key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={dateFieldStyles}>
            <DatePicker
              label={strings.from.label}
              format={DATE_FORMAT}
              value={fromDate}
              slotProps={{
                textField: {
                  size: "small",
                  style: { minWidth: 150, maxWidth: 200 },
                },
              }}
              onChange={(value) => {
                setPage(1);
                setFromDate(value);
              }}
            />
          </Box>
          <Box sx={dateFieldStyles}>
            <DatePicker
              label={strings.to.label}
              format={DATE_FORMAT}
              value={toDate}
              minDate={fromDate || undefined}
              slotProps={{
                textField: {
                  size: "small",
                  style: { minWidth: 150, maxWidth: 200 },
                },
              }}
              onChange={(value) => {
                setPage(1);
                setToDate(value);
              }}
            />
          </Box>
        </LocalizationProvider>
        <button
          onClick={() => {
            setPage(1);
            setStatusFilter(ALL_STATUS);
            setFromDate(null);
            setToDate(null);
          }}
          className="hover:underline mt-10 ml-10 inline-block cursor-pointer text-[14px] text-slate-500"
        >
          {strings.clearFilter.label}
        </button>
      </div>

      {loading && logs.length === 0 ? (
        <Box className="flex w-full justify-center py-[30px]">
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <div className="w-full py-[30px] text-center text-[#999999]">
          {strings.empty.label}
        </div>
      ) : (
        <div className="w-full">
          <div className="flex w-full border-b border-gray-200 pb-[8px] text-[12px] font-semibold text-[#4A4A4A]">
            <div className="basis-[65px] shrink-0" />
            <div className="basis-[160px] shrink-0">
              {strings.columns.status.label}
            </div>
            <div className="basis-[180px] shrink-0">
              {strings.columns.date.label}
            </div>
            <div className="basis-[100px] shrink-0">
              {strings.columns.trigger.label}
            </div>
            <div className="basis-[180px] shrink-0">
              {strings.columns.triggeredBy.label}
            </div>
            <div className="basis-[100px] shrink-0">
              {strings.columns.assets.label}
            </div>
            <div className="grow" />
          </div>
          {logs.map((log) => (
            <div
              key={log.id}
              className="flex w-full items-center border-b border-gray-100 py-[10px] text-[13px] text-[#4A4A4A]"
            >
              <div className="basis-[65px] shrink-0">
                <SyncLogThumbnails
                  assets={log.assets ?? []}
                  onClick={() => setDetailsLogId(log.id)}
                />
              </div>
              <div className="flex basis-[160px] shrink-0 items-center gap-[6px]">
                <CircleIcon
                  className={`!h-[12px] !w-[12px] ${getSyncStatusColor(
                    log.status
                  )}`}
                />
                <span>{log.status}</span>
              </div>
              <div className="basis-[180px] shrink-0">
                {formatSyncDate(effectiveDate(log))}
              </div>
              <div className="basis-[100px] shrink-0">
                {log.triggerType && (
                  <Chip size="small" label={log.triggerType} />
                )}
              </div>
              <div className="basis-[180px] shrink-0 truncate">
                {log.triggeredByName || "-"}
              </div>
              <div className="basis-[100px] shrink-0">
                {log.assets?.length ?? 0}
              </div>
              <div className="flex grow justify-end gap-[8px]">
                {me?.isAdmin &&
                  log.status === MetaphoreCollectionSyncLogStatus.Scheduled && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        className="capitalize rounded-full"
                        startIcon={<PlayArrowIcon />}
                        disabled={runNowLoading || cancelLoading}
                        onClick={() => handleRunNow(log.id)}
                      >
                        {strings.runNow.label}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        className="capitalize rounded-full"
                        startIcon={<CancelIcon />}
                        disabled={runNowLoading || cancelLoading}
                        onClick={() =>
                          setCancelModal({ isOpen: true, id: log.id })
                        }
                      >
                        {strings.cancel.label}
                      </Button>
                    </>
                  )}
                <Button
                  size="small"
                  className="capitalize"
                  onClick={() => setDetailsLogId(log.id)}
                >
                  {strings.details.label}
                </Button>
              </div>
            </div>
          ))}
          <Pagination
            sx={{
              "& .MuiButtonBase-root.Mui-selected": {
                backgroundColor: "#4A4A4A",
                color: "white",
              },
            }}
            onChange={(_, newPage: number) => setPage(newPage)}
            className="mt-10 flex w-full justify-center"
            count={Math.ceil(totalCount / PAGE_SIZE)}
            page={page}
            size="large"
          />
        </div>
      )}

      {cancelModal?.isOpen && (
        <ConfirmDialog
          isOpen={cancelModal.isOpen}
          header={strings.cancelModal.header}
          text={strings.cancelModal.text}
          onClose={() => setCancelModal(undefined)}
          onConfirm={() => handleCancel(cancelModal.id)}
        />
      )}
      {detailsLog && (
        <SyncLogDetailsModal
          isOpen
          log={detailsLog}
          canManage={me?.isAdmin || false}
          actionsDisabled={runNowLoading || cancelLoading}
          onRunNow={(id) => {
            setDetailsLogId(undefined);
            handleRunNow(id);
          }}
          onCancel={(id) => {
            setDetailsLogId(undefined);
            setCancelModal({ isOpen: true, id });
          }}
          onClose={() => setDetailsLogId(undefined)}
        />
      )}
    </div>
  );
}

type LabelString = { label: string };

type LocalSchema = {
  status: LabelString;
  allStatus: LabelString;
  from: LabelString;
  to: LabelString;
  clearFilter: LabelString;
  empty: LabelString;
  runNow: LabelString;
  cancel: LabelString;
  details: LabelString;
  columns: {
    status: LabelString;
    date: LabelString;
    trigger: LabelString;
    triggeredBy: LabelString;
    assets: LabelString;
  };
  cancelSuccess: LabelString;
  cancelError: LabelString;
  runNowSuccess: LabelString;
  runNowError: LabelString;
  cancelModal: { header: string; text: string };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    status: { label: "Status" },
    allStatus: { label: "All" },
    from: { label: "From" },
    to: { label: "To" },
    clearFilter: { label: "Reset filter" },
    empty: { label: "No syncs found for this collection." },
    runNow: { label: "Run now" },
    cancel: { label: "Cancel" },
    details: { label: "Details" },
    columns: {
      status: { label: "Status" },
      date: { label: "Scheduled / synced at" },
      trigger: { label: "Trigger" },
      triggeredBy: { label: "Triggered by" },
      assets: { label: "Assets" },
    },
    cancelSuccess: { label: "Scheduled sync canceled." },
    cancelError: { label: "Failed to cancel the scheduled sync." },
    runNowSuccess: { label: "Sync executed." },
    runNowError: { label: "Failed to run the sync." },
    cancelModal: {
      header: "Cancel scheduled sync",
      text: "Are you sure you want to cancel this scheduled sync? The assets will not be synced until they are added again or a sync is started manually.",
    },
  },
};
